import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { FilterProps } from "../../types/AppTypes";
import { useNotification } from "../notification.hook";
import { PotentialMatrixFilters } from "../usePotentialMatrixFilters.hook";
import PotentialMatrixDevelopmentService from "../../services/api/potentialMatrix/PotentialMatrixDevelopment.service";
import {
  MATRIX_COLLABORATORS_PAGE_SIZE,
  RefreshReactQuery,
} from "../../constants/AppConstants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../../constants/Dictionary";
import { MatrixDevelopmentItem } from "../../types/api/potentialMatrix.type";
import { PotentialMatrixFilterKeyEnum } from "../../types/api/filters.type";
import PotentialMatrixDevelopmentCollaboratorsService from "../../services/api/potentialMatrix/PotentialMatrixDevelopmentCollaborators.service";
import { useDebounce } from "use-debounce";
import { TypeMatrixEnum } from "../../constants/PotentialMatrixConstants";

interface usePotentialMatrixDevelopmentProps {
  filters: PotentialMatrixFilters;
  createQueryParams: () => FilterProps[];
  enabled: boolean;
}

export const usePotentialMatrixDevelopment = ({
  filters,
  createQueryParams,
  enabled,
}: usePotentialMatrixDevelopmentProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ["get-potential-matrix-development", filters],
    queryFn: ({ signal }) =>
      PotentialMatrixDevelopmentService.get(createQueryParams(), signal),
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return {
    data: data?.data ?? [],
    internalGradeStats: data?.internalGradeStats ?? [],
    isLoading,
    isFetching,
  };
};

interface usePotentialMatrixDevelopmentChartStatsProps {
  filters: PotentialMatrixFilters;
  enabled?: boolean;
}

export const usePotentialMatrixDevelopmentChartStats = ({
  filters,
  enabled,
}: usePotentialMatrixDevelopmentChartStatsProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      "get-potential-matrix-development-stats",
      filters.practice,
      filters.cc,
      filters.type,
      filters.year,
    ],
    queryFn: ({ signal }) =>
      PotentialMatrixDevelopmentService.getChartStats(
        [
          { key: "year", value: [filters.year] },
          {
            key: "type",
            value: [filters.type.value],
          },
          ...(filters.practice.length > 0
            ? [
                {
                  key: "practice",
                  value: filters.practice,
                },
              ]
            : []),
          ...(filters.cc.length > 0
            ? [
                {
                  key: "cc",
                  value: filters.cc,
                },
              ]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

interface useDevelopmentMatrixDetailsProps {
  enabled: boolean;
  searchDebouncedValue: string;
  item: MatrixDevelopmentItem;
  pageNumber: number;
  filters: PotentialMatrixFilters;
}
export const useDevelopmentMatrixDetails = ({
  enabled,
  searchDebouncedValue,
  item,
  pageNumber,
  filters,
}: useDevelopmentMatrixDetailsProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      "get-matrix-development-collaborators",
      searchDebouncedValue,
      item,
      pageNumber,
      filters,
    ],
    queryFn: ({ signal }) =>
      PotentialMatrixDevelopmentCollaboratorsService.list(
        MATRIX_COLLABORATORS_PAGE_SIZE,
        pageNumber,
        [
          { key: PotentialMatrixFilterKeyEnum.YEAR, value: [filters.year] },
          {
            key: PotentialMatrixFilterKeyEnum.TYPE,
            value: [filters.type.value],
          },
          ...(filters.practice.length > 0
            ? [
                {
                  key: PotentialMatrixFilterKeyEnum.PRACTICE,
                  value: filters.practice,
                },
              ]
            : []),
          ...(filters.cc.length > 0
            ? [
                {
                  key: PotentialMatrixFilterKeyEnum.COMPETENCE,
                  value: filters.cc,
                },
              ]
            : []),
          ...(filters.hiddenCollaborators.length > 0
            ? [
                {
                  key: PotentialMatrixFilterKeyEnum.HIDDEN_COLLABORATORS,
                  value: filters.hiddenCollaborators,
                },
              ]
            : []),
          ...[
            { key: "screen", value: [item!.screen] },
            { key: "internalGrade", value: [item!.internalGrade] },
          ],
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

interface useMatrixDevelopmentCollaboratorsProps {
  filters: PotentialMatrixFilters;
  enabled: boolean;
}

export const useMatrixDevelopmentCollaborators = ({
  filters,
  enabled,
}: useMatrixDevelopmentCollaboratorsProps) => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey: [
        "get-matrix-development-collaborators",
        filters,
        debouncedSearch,
      ],
      queryFn: ({ pageParam, signal }) =>
        PotentialMatrixDevelopmentCollaboratorsService.list(
          MATRIX_COLLABORATORS_PAGE_SIZE,
          pageParam as number,
          [
            { key: PotentialMatrixFilterKeyEnum.YEAR, value: [filters.year] },
            {
              key: PotentialMatrixFilterKeyEnum.TYPE,
              value: [filters.type.value],
            },
            {
              key: PotentialMatrixFilterKeyEnum.PRACTICE,
              value: filters.practice,
            },
            {
              key: PotentialMatrixFilterKeyEnum.COMPETENCE,
              value: filters.cc,
            },
            ...(debouncedSearch
              ? [{ key: "search", value: [debouncedSearch] }]
              : []),
          ],
          signal,
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        allPages.length * MATRIX_COLLABORATORS_PAGE_SIZE <
        lastPage.pagination.total
          ? allPages.length
          : undefined,
      staleTime: RefreshReactQuery.MODERATE,
      enabled,
    });

  useEffect(() => {
    if (isError) notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
  }, [isError]);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const clearSearch = useCallback(() => setSearch(""), []);

  return {
    data: items,
    initLoading: isLoading,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    fetchingMore: isFetching && (data?.pages.length ?? 0) > 0,
    search,
    setSearch,
    clearSearch,
  };
};

interface useMatrixDevelopmentDownloadProps {
  filters: PotentialMatrixFilters;
}

export const useMatrixDevelopmentDownload = ({
  filters,
}: useMatrixDevelopmentDownloadProps) => {
  const { notification } = useNotification();

  const { mutateAsync } = useMutation({
    mutationFn: (downloadLimit: number) =>
      PotentialMatrixDevelopmentService.get([
        { key: "year", value: [filters.year] },
        { key: "type", value: [TypeMatrixEnum.FINAL_EVALUATION] },
        { key: "collaboratorsLimit", value: [downloadLimit] },
        { key: "collaboratorsSortType", value: ["asc"] },
        { key: "practice", value: [filters.practice[0]] },
        { key: "competence", value: [filters.cc[0]] },
      ]),
    onError: (error) => {
      console.error(`${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync };
};
